import React, { useEffect, useState } from 'react';
import { materialActions, materialSelectors } from 'store/material';
import { useDispatch, useSelector } from 'react-redux';

import AddMaterialDialog from './AddMaterialDialog';
import BatchType from 'components/BatchType';
import Box from '@mui/material/Box';
import { Button } from '@acheloisbiosoftware/absui.core';
import DataTable from 'components/DataTable';
import Error from 'components/Error';
import InfoTable from 'components/InfoTable';
import Link from '@mui/material/Link';
import LoadingPage from 'components/LoadingPage';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { formatDate } from 'utils/date.utils';
import { useParams } from 'react-router-dom';

function Material() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const params = useParams();
  const material = useSelector(materialSelectors.selectMaterial);
  const fetchedMaterial = useSelector(materialSelectors.selectPageFetched);

  useEffect(() => {
    dispatch(materialActions.fetchMaterial(params.materialId));
    return () => dispatch(materialActions.resetPage());
  }, [params, dispatch]);

  if (!fetchedMaterial) {
    return <LoadingPage />;
  }
  if (!material || !material.material_id) {
    return (
      <Error>Material not found!</Error>
    );
  }
  const {
    vendor,
    catalog_no,
    name,
    batch_type,
    description,
    purchase_url,
    custom_props,
    material_lots,
  } = material;
  const infoData = [
    {
      key: 'vendor',
      title: 'Vendor',
      content: vendor,
    },
    {
      key: 'catalog_no',
      title: 'Catalog #',
      content: catalog_no,
    },
    {
      key: 'description',
      title: 'Description',
      content: description,
    },
    {
      key: 'url',
      title: 'Product Website',
      content: <Link href={purchase_url} target='_blank'>{vendor} {catalog_no}</Link>,
    },
    ...custom_props.map(({ title, value }, idx) => ({
      key: `custom_prop${idx}`,
      title,
      content: value,
    })),
    {
      key: 'experiment',
      title: 'Experiment',
      content: (<BatchType type={batch_type} />),
    },
  ];

  const columns = [
    { key: 'lot_no', title: 'Lot #' },
    { key: 'date_received', title: 'Date Received' },
    { key: 'received_by', title: 'Received By' },
    { key: 'storage_location', title: 'Storage Location' },
    { key: 'custom_props', title: 'Additional Properties' },
  ];
  const lotData = material_lots.map((lot) => ({
    key: `material_lot_${lot.material_lot_id}`,
    lot_no: lot.lot_no,
    date_received: formatDate(lot.date_received),
    received_by: lot.received_by?.name,
    storage_location: lot.storage_location,
    custom_props: lot.custom_props.map((prop) => (
      <Typography key={prop.title} variant='body2'>
        {prop.title}: {prop.value}
      </Typography>
    )),
  }));

  const sxPaper = { p: 3, color: 'text.secondary', mb: 2 };
  const sxCentered = { textAlign: 'center', m: 'auto', justifyContent: 'center' };

  return (
    <Box sx={{ m: 'auto', mt: '64px', py: 2, px: 4, maxWidth: 1000 }}>
      <Paper sx={sxPaper} variant='outlined'>
        <Typography variant='h4' sx={sxCentered}>{name}</Typography>
        <InfoTable
          data={infoData}
          borderTop
          tableProps={{ sx: { ...sxCentered, maxWidth: 500, mt: 1.5 }}}
        />
      </Paper>
      <Paper sx={sxPaper} variant='outlined'>
        <Typography variant='h5' sx={sxCentered}>Material Lots</Typography>
        <DataTable
          columns={columns}
          data={lotData}
          containerProps={{ sx: { m: 1.5 }}}
        />
        <Box sx={{ width: 1, display: 'flex' }}>
          <Button
            variant='contained'
            sx={{ mr: 1.5, ml: 'auto' }}
            onClick={() => setDialogOpen(true)}
          >
            Create new
          </Button>
          <AddMaterialDialog
            material={material}
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default Material;
